/**
 * The wire contract between any kikik.id client (web today, Android later) and
 * the authoritative game server.
 *
 * Every message is JSON-safe and carries a protocol version. Clients are never
 * authoritative: they send commands, the server sends back redacted state.
 */

import type {
  Action,
  ActionError,
  ClaimType,
  DiscardRef,
  GameEvent,
  GamePhase,
  GameResult,
  Meld,
  PlayerId,
  SeatWind,
  Tile,
} from "@kikik/mahjong-core";

/** Bump on any breaking change to the message shapes below. */
export const PROTOCOL_VERSION = 1;

export type SessionId = string;
export type RoomCode = string;
export type ResumeToken = string;

export type SeatOccupantKind = "human" | "bot" | "empty";

export interface SeatView {
  seat: PlayerId;
  wind: SeatWind | null;
  occupant: SeatOccupantKind;
  displayName: string | null;
  connected: boolean;
  /** True for the seat belonging to the receiving client. */
  you: boolean;
  host: boolean;
}

export interface RoomView {
  code: RoomCode;
  ruleSetId: string;
  started: boolean;
  seats: SeatView[];
  /** How many human seats are still needed before the host can start. */
  humanCount: number;
}

/** Everything about a player that every client is allowed to see. */
export interface PublicPlayerView {
  id: PlayerId;
  seat: SeatWind;
  /** Concealed tile count only: never the tiles themselves. */
  handCount: number;
  hasDrawnTile: boolean;
  discards: Tile[];
  melds: Meld[];
}

/** Only ever sent to the player it belongs to. */
export interface PrivatePlayerView {
  id: PlayerId;
  hand: Tile[];
  drawnTile: Tile | null;
}

export interface PendingClaimsView {
  discard: DiscardRef;
  waitingOn: PlayerId[];
  /** Claims are announced out loud, but the claimer's tiles stay hidden. */
  declared: { playerId: PlayerId; type: ClaimType }[];
}

export interface PlayerGameView {
  protocolVersion: number;
  roomCode: RoomCode;
  ruleSetId: string;
  you: PlayerId;
  phase: GamePhase;
  turn: PlayerId;
  dealer: PlayerId;
  wallRemaining: number;
  deadWallRemaining: number;
  lastDiscard: DiscardRef | null;
  pendingClaims: PendingClaimsView | null;
  players: PublicPlayerView[];
  self: PrivatePlayerView;
  /** What this client may legally do right now. */
  legalActions: Action[];
  actionCount: number;
  result: GameResult | null;
}

export interface ResumeCredentials {
  roomCode: RoomCode;
  sessionId: SessionId;
  resumeToken: ResumeToken;
}

export type ClientCommand =
  | {
      type: "hello";
      protocolVersion: number;
      displayName?: string;
      resume?: ResumeCredentials;
    }
  | { type: "create-room"; ruleSetId?: string; displayName?: string }
  | { type: "join-room"; roomCode: RoomCode; displayName?: string }
  | { type: "leave-room" }
  | { type: "add-bot"; seat?: PlayerId }
  | { type: "start-game"; seed?: string }
  | { type: "action"; action: Action }
  | { type: "ping" };

export type ProtocolErrorCode =
  | "BAD_MESSAGE"
  | "UNSUPPORTED_PROTOCOL"
  | "NO_SESSION"
  | "ROOM_NOT_FOUND"
  | "ROOM_FULL"
  | "ALREADY_IN_ROOM"
  | "NOT_IN_ROOM"
  | "NOT_HOST"
  | "GAME_ALREADY_STARTED"
  | "GAME_NOT_STARTED"
  | "NOT_YOUR_SEAT"
  | "RESUME_FAILED";

export type ServerEvent =
  | {
      type: "welcome";
      protocolVersion: number;
      sessionId: SessionId;
      resumeToken: ResumeToken;
      /** Set when the welcome came from a successful resume. */
      resumed: boolean;
    }
  | { type: "room-state"; room: RoomView }
  | { type: "game-started"; room: RoomView; view: PlayerGameView }
  | { type: "game-state"; view: PlayerGameView; events: GameEvent[] }
  | { type: "game-ended"; view: PlayerGameView; result: GameResult }
  | { type: "action-rejected"; error: ActionError }
  | { type: "error"; code: ProtocolErrorCode; message: string }
  | { type: "pong" };
